import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ShieldCheck, KeyRound, AlertCircle, LifeBuoy } from 'lucide-react';

/**
 * TOTP login challenge — clean EG3 brand chrome (iter114s.68).
 *
 * Replaces the previous medieval theme (knight artwork +
 * Cormorant Garamond italics + "Present Thy Royal Seal" copy) with
 * the standard EG3 surface — pale-green gradient backdrop, the
 * shield-check icon, lucide icons, plain conversational copy.
 *
 * Functional logic (httpOnly `eg3_2fa_challenge` cookie, /auth/2fa-context
 * fallback for refresh, /totp/verify + /totp/verify-backup endpoints,
 * post-2FA redirect to /admin or the fromPath) is unchanged. All
 * data-testids are preserved.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOTPChallenge = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();

  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('totp'); // 'totp' | 'backup'

  const fromPath = location.state?.from?.pathname;
  const post2faTarget = fromPath && fromPath !== '/login' ? fromPath : '/admin';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/2fa-context`, { withCredentials: true });
        if (cancelled) return;
        if (res.data?.email && !email) setEmail(res.data.email);
      } catch {
        if (cancelled) return;
        setError('No active sign-in. Return to the sign-in page first.');
      }
    })();
    return () => { cancelled = true; };
  }, [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'totp' && !/^\d{6}$/.test(code)) {
      setError('Enter exactly 6 digits.');
      return;
    }
    if (mode === 'backup' && backupCode.trim().length < 6) {
      setError('Backup code is too short.');
      return;
    }
    setLoading(true);
    try {
      const endpoint = mode === 'totp' ? '/api/auth/totp/verify' : '/api/auth/totp/verify-backup';
      const body = mode === 'totp'
        ? { code }
        : { backup_code: backupCode.trim() };
      await axios.post(`${API_URL}${endpoint}`, body, { withCredentials: true });
      if (refreshUser) await refreshUser();
      navigate(post2faTarget, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 429) {
        setError('Too many attempts. Please wait 15 minutes and try again.');
      } else {
        setError(detail || 'Invalid code. Check your authenticator and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#E8F1E6] via-[#F4F7F2] to-white flex items-center justify-center px-4 py-10"
      data-testid="totp-challenge-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <ShieldCheck className="w-12 h-12 text-[#2F5D3A] mx-auto mb-2" />
          <h1
            className="text-3xl md:text-4xl font-bold text-[#14281D] tracking-tight"
            data-testid="totp-challenge-heading"
          >
            {mode === 'totp' ? 'Two-factor authentication' : 'Use a backup code'}
          </h1>
          <p className="mt-2 text-sm md:text-base text-[#1F3D2C]/75">
            {mode === 'totp'
              ? 'Enter the 6-digit code from your authenticator app.'
              : 'Single-use backup codes — each works only once.'}
          </p>
          {email && (
            <p className="text-sm text-gray-500 mt-1" data-testid="totp-challenge-account">
              Account: {email}
            </p>
          )}
        </div>

        <div
          className="bg-white rounded-2xl shadow-xl p-7 border border-[#E5E7EB]"
          data-testid="totp-challenge-card"
        >
          {error && (
            <div
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700 text-sm"
              data-testid="totp-challenge-error"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'totp' ? (
              <div>
                <label className="block text-sm font-medium text-[#14281D] mb-1.5" htmlFor="tch-code">
                  6-digit code
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  <input
                    id="tch-code"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="••••••"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent text-2xl font-mono tracking-[0.3em] text-center"
                    data-testid="totp-challenge-code-input"
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-[#14281D] mb-1.5" htmlFor="tch-backup">
                  Backup code
                </label>
                <div className="relative">
                  <LifeBuoy className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  <input
                    id="tch-backup"
                    type="text"
                    maxLength={32}
                    autoFocus
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                    placeholder="ABC12345"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent text-base font-mono tracking-widest text-center"
                    data-testid="totp-challenge-backup-input"
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#6FBF73] hover:bg-[#4A7C59] text-white py-3 text-base font-semibold rounded-full transition-colors"
              data-testid="totp-challenge-submit-button"
            >
              {loading ? 'Verifying…' : 'Verify & continue'}
            </Button>
          </form>

          <div className="mt-5 flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => { setMode(mode === 'totp' ? 'backup' : 'totp'); setError(''); setCode(''); setBackupCode(''); }}
              className="text-[#2F5D3A] hover:text-[#6FBF73] hover:underline font-medium"
              data-testid="totp-challenge-toggle-mode"
            >
              {mode === 'totp' ? 'Use a backup code instead' : 'Use authenticator app instead'}
            </button>
            <Link
              to="/login"
              className="text-gray-500 hover:text-gray-700"
              data-testid="totp-challenge-back-to-login"
            >
              ← Back
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TOTPChallenge;
